<template>
  <div>
    <div id="trend-plot-grid">
      <svg
        id="trend-title-text"
        viewBox="0 0 20.9 1"
        preserveAspectRatio="xMinYMin"
      >
        <text font-size="0.60pt" x="0.25" y="0.70" fill="#156560">
          Trend Data (last 5 minutes)
        </text>
      </svg>
      <ButtonGroup
        id="toggle-button-group"
        :navs="navs"
        @selected-changed="selected"
      ></ButtonGroup>
      <PercentChangePlot
        id="percent-change-plot"
        :markers="percentMarkers"
        :signals="percentSignal"
        :yMax="yMax"
        :yMin="yMin"
        :xMax="xMax"
        :xMin="xMin"
      ></PercentChangePlot>
      <div id="y-scale-container">
        <svg id="y-scale-max-container" viewBox="0 0 10 10">
          <text x="2" y="3" font-size="2pt" text-align="right">
            {{ maxPercent }}
          </text>
        </svg>
        <svg id="y-scale-min-container" viewBox="0 0 10 10">
          <text x="2" y="8" font-size="2pt" text-align="right">
            {{ minPercent }}
          </text>
        </svg>
      </div>
      <div id="numeric-change">
        <svg
          id="numerics-title-svg"
          viewBox="0 0 75 50"
          preserveAspectRatio="xMidYMid"
        >
          <text x="37.5" y="18" font-size="18" text-anchor="middle">
            <tspan x="37.5">Change over last</tspan>
            <tspan x="37.5" dy="1.2em">5 mins</tspan>
          </text>
        </svg>
        <svg
          v-if="currentSignal === 'CO/HR/SVR'"
          id="numerics-params-svg"
          viewBox="0 0 75 25"
          preserveAspectRatio="xMidYMid"
        >
          <text x="37.5" y="16" font-size="18" text-anchor="middle">
            <tspan x="37">CO</tspan>
            <tspan dx="2em">HR</tspan>
            <tspan dx="2em">SVR</tspan>
          </text>
        </svg>
        <svg
          v-else
          id="numerics-params-svg"
          viewBox="0 0 75 25"
          preserveAspectRatio="xMidYMid"
        >
          <text x="37.5" y="16" font-size="18" text-anchor="middle">
            <tspan x="37">{{ currentSignal }}</tspan>
          </text>
        </svg>
      </div>
      <div v-if="currentSignal === 'CO/HR/SVR'" id="percent-numerics">
        <PercentNumeric
          id="co-numeric"
          :min="yMin"
          :max="yMax"
          :value="coNumeric"
          color="#1B77D2"
        ></PercentNumeric>
        <PercentNumeric
          id="hr-numeric"
          :min="yMin"
          :max="yMax"
          :value="hrNumeric"
          color="#fcbc36"
        ></PercentNumeric>
        <PercentNumeric
          :min="yMin"
          :max="yMax"
          :value="svrNumeric"
          color="#d345da"
        ></PercentNumeric>
      </div>
      <div v-else id="percent-numeric-single">
        <PercentNumeric
          id="percent-numeric-single-item"
          :min="yMin"
          :max="yMax"
          :value="singleNumeric"
          :color="singleNumericColor"
        ></PercentNumeric>
      </div>
    </div>
  </div>
</template>

<script>
import PercentChangePlot from '../components/PercentChangePlot'
import ButtonGroup from '../components/ButtonGroup'
import PercentNumeric from '../components/PercentNumeric'

export default {
  data() {
    return {
      navs: [
        {
          id: 0,
          text: 'CO/HR/SVR',
          color: '#707070'
        },
        {
          id: 1,
          text: 'CO',
          color: '#1B77D2'
        },
        {
          id: 2,
          text: 'HR',
          color: '#fcbc36'
        },
        {
          id: 3,
          text: 'SVR',
          color: '#d345da'
        }
      ],
      currentSignal: 'CO/HR/SVR'
    }
  },
  props: {
    xMin: {
      type: Number,
      required: true
    },
    xMax: {
      type: Number,
      required: true
    },
    coSignal: {
      type: Array,
      required: true
    },
    hrSignal: {
      type: Array,
      required: true
    },
    svrSignal: {
      type: Array,
      required: true
    },
    markers: {
      type: Array,
      required: false,
      default: null
    }
  },
  computed: {
    singleNumericColor() {
      switch (this.currentSignal) {
        case 'CO':
          return '#1B77D2'
        case 'HR':
          return '#fcbc36'
        case 'SVR':
          return '#d345da'
        default:
          return '#1B77D2'
      }
    },
    singleNumeric() {
      switch (this.currentSignal) {
        case 'CO':
          return this.coNumeric
        case 'HR':
          return this.hrNumeric
        case 'SVR':
          return this.svrNumeric
        default:
          return this.coNumeric
      }
    },
    coNumeric() {
      let baseline = this.signalBaseline(this.coSignal)
      return Math.round(
        ((this.coSignal[this.coSignal.length - 1][1] - baseline) / baseline) *
          100
      )
    },
    hrNumeric() {
      let baseline = this.signalBaseline(this.hrSignal)
      return Math.round(
        ((this.hrSignal[this.hrSignal.length - 1][1] - baseline) / baseline) *
          100
      )
    },
    svrNumeric() {
      let baseline = this.signalBaseline(this.svrSignal)
      return Math.round(
        ((this.svrSignal[this.svrSignal.length - 1][1] - baseline) / baseline) *
          100
      )
    },
    percentSignal() {
      switch (this.currentSignal) {
        case 'CO/HR/SVR':
          return this.percentCombo
        case 'CO':
          return this.percentCO
        case 'HR':
          return this.percentHR
        case 'SVR':
          return this.percentSVR
        default:
          return this.percentCombo
      }
    },
    percentCombo() {
      return [
        { points: this.convertToPercent(this.coSignal), color: '#1B77D2' },
        { points: this.convertToPercent(this.hrSignal), color: '#fcbc36' },
        { points: this.convertToPercent(this.svrSignal), color: '#d345da' }
      ]
    },
    percentCO() {
      return [
        { points: this.convertToPercent(this.coSignal), color: '#1B77D2' }
      ]
    },
    percentHR() {
      return [
        { points: this.convertToPercent(this.hrSignal), color: '#fcbc36' }
      ]
    },
    percentSVR() {
      return [
        { points: this.convertToPercent(this.svrSignal), color: '#d345da' }
      ]
    },
    yMagnitude() {
      //todo: check max over all displayed signals not just first in list, to handle comboSignal
      let maxAbs = 50
      this.percentSignal.forEach(signal => {
        let signalMin = Math.abs(this.signalMin(signal.points))
        let signalMax = Math.abs(this.signalMax(signal.points))
        if (signalMin > maxAbs) maxAbs = signalMin
        if (signalMax > maxAbs) maxAbs = signalMax
      })
      return maxAbs
    },
    yMax() {
      return this.yMagnitude
    },
    yMin() {
      return -this.yMagnitude
    },
    percentMarkers() {
      // Checking within range of percentSignal as we can only plot
      // markers if they fall on the signal
      let markersInRange = this.markers.filter(
        marker =>
          marker.point[0] >= this.xMin &&
          marker.point[0] <= this.xMax &&
          marker.point[0] >= this.percentSignal[0].points[0][0] &&
          marker.point[0] <=
            this.percentSignal[0].points[
              this.percentSignal[0].points.length - 1
            ][0]
      )
      if (markersInRange.length === 0) {
        return []
      }
      return markersInRange.map(marker => {
        let markerY = this.percentSignal[0].points.find(
          point => point[0] === marker.point[0]
        )[1]
        return {
          point: [marker.point[0], markerY],
          type: marker.type,
          shortName: marker.shortName,
          isFlipped: markerY > 0
        }
      })
    },
    maxPercent() {
      return '+' + Math.round(this.yMax) + '%'
    },
    minPercent() {
      return Math.round(this.yMin) + '%'
    },
    currentColor() {
      return '#1B77D2'
    }
  },
  components: {
    PercentChangePlot,
    ButtonGroup,
    PercentNumeric
  },
  methods: {
    convertToPercent(signal) {
      let baseline = this.signalBaseline(signal)
      return signal.map(point => {
        let percent = ((point[1] - baseline) / baseline) * 100
        return [point[0], percent]
      })
    },
    signalBaseline(signal) {
      for (let i = 0; i < signal.length; i++) {
        if (!isNaN(signal[i][1])) {
          return signal[i][1]
        }
      }
      return NaN
    },
    selected(button) {
      this.currentSignal = button
    },
    signalMin(signal) {
      let min = 0
      signal.forEach(element => {
        if (isFinite(element[1]) && element[1] < min) {
          min = element[1]
        }
      })
      return min
    },
    signalMax(signal) {
      let max = 0
      signal.forEach(element => {
        if (isFinite(element[1]) && element[1] > max) {
          max = element[1]
        }
      })
      return max
    }
  }
}
</script>

<style scoped>
#trend-plot-grid {
  background-color: white;
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: 20% 17.6% 62.4%;
  grid-template-columns: 6% 50% 25% 19%;
}
#trend-title-text {
  grid-row: 1;
  grid-column-start: 1;
  grid-column-end: 4;
}
#toggle-button-group {
  grid-row: 2;
  grid-column: 2;
}
#percent-change-plot {
  padding-top: 1.5%;
  padding-bottom: 1.5%;
  grid-row: 3;
  background: #eeeeee;
  grid-column-start: 2;
  grid-column-end: 4;
}
#y-scale-container {
  grid-row: 3;
  grid-column: 1;
  position: relative;
}
#y-scale-max-container {
  position: absolute;
  top: 0;
  left: 0;
}
#y-scale-min-container {
  position: absolute;
  bottom: 0;
  left: 0;
}
#numeric-change {
  grid-row-start: 1;
  grid-row-end: 3;
  grid-column: 4;
  background-color: #d3d3d3;
  display: flex;
  flex-direction: column;
}
#numerics-title-svg {
  height: 66%;
  width: 100%;
}
#numerics-params-svg {
  height: 33%;
  width: 100%;
}
#percent-numerics {
  grid-row: 3;
  grid-column: 4;
  display: flex;
  justify-content: space-between;
  background-color: #d3d3d3;
}
#percent-numeric-single {
  grid-row: 3;
  grid-column: 4;
  display: grid;
  grid-template-columns: 25% 50% 25%;
  grid-template-rows: 100%;
  background-color: #d3d3d3;
}
#percent-numeric-single-item {
  grid-column: 2;
}
#co-numeric {
  border-right: 1px solid black;
}
#hr-numeric {
  border-right: 1px solid black;
}
</style>
